import API from "./const";

export enum VerifySession {
  MISSING = 1,
  TOS = 2,
  EXPIRED = 3
}

type SessionExpiredHandler = () => void;
let sessionExpiredHandler: SessionExpiredHandler | null = null;

const SESSION_EXPIRED_DETAILS = new Set([
  "Credentials missing",
  "Invalid or expired credentials",
]);

export function isSessionExpiredDetail(detail: string): boolean {
  return SESSION_EXPIRED_DETAILS.has(detail);
}

export async function isSessionExpiredResponse(response: Response): Promise<boolean> {
  if (response.status !== 403) {
    return false;
  }
  try {
    const body = await response.clone().json();
    const detail =
      typeof body.detail === "string"
        ? body.detail
        : typeof body.message === "string"
          ? body.message
          : "";
    return isSessionExpiredDetail(detail);
  } catch {
    return false;
  }
}

export function setSessionExpiredHandler(handler: SessionExpiredHandler | null) {
  sessionExpiredHandler = handler;
}

export async function handleSessionExpired(): Promise<void> {
  removeLocalSession();
  try {
    await logout();
  } catch {
    // Best-effort cookie clear; auth state is cleared regardless.
  }
  sessionExpiredHandler?.();
}

/** Authenticated API fetch; clears auth state when the session is invalid (403). */
export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const response = await fetch(input, {
    ...init,
    credentials: init?.credentials ?? "include",
  });
  if (await isSessionExpiredResponse(response)) {
    await handleSessionExpired();
  }
  return response;
}

export interface User {
  uuid: string;
  customer_id: string | null;
  date_created: Date;
  subscription_id: string | null;
  transaction_id: string | null;
  has_team: boolean;
  end_of_trial: Date | null;
  is_admin?: boolean;
  /** First linked email (from whoami); optional for older cached sessions */
  email?: string | null;
}

export async function whoami() {
  try {
    const response = await fetch(`${API}/whoami`, {
      method: 'GET',
      credentials: 'include'
    });

    if (response.status === 309) {
      return VerifySession.TOS;
    }
    else if (!response.ok) {
      if (response.status === 403) {
        await handleSessionExpired();
        return VerifySession.EXPIRED;
      }
      removeLocalSession();
      return VerifySession.MISSING;
    }
    let user = await response.json();
    
    user.date_created = new Date(user.date_created);
    user.end_of_trial = new Date(user.end_of_trial);
    setLocalSession(user);
    return user;

  } catch (error) {
    removeLocalSession()
    return VerifySession.MISSING;
  }
}

export async function optimisticIsConnected(): Promise<boolean> {
  const session_id = getLocalSession();
  if(session_id == null) {
    let response = await whoami();
    if(typeof response === 'number'){
      return false;
    }
  }
  return true;
}

export function setLocalSession(user: User) {
  sessionStorage.setItem("user-id", user.uuid)
  return sessionStorage.setItem("session-id", JSON.stringify(user))
}

export function getLocalSession() {
  // Implement the logic to retrieve the session ID from localStorage or wherever it is stored
  return sessionStorage.getItem("session-id")
}

export function lastCheckedSession(): Date | null {
  const lastCheckedSession = sessionStorage.getItem("last-checked-session")
  if (lastCheckedSession == null) {
    return null;
  }
  return new Date(lastCheckedSession);
}

export function setLastCheckedSession(date: Date) {
  sessionStorage.setItem("last-checked-session", date.toISOString())
}

export function getLocalUserId(){
  return sessionStorage.getItem("user-id")
}

export function removeLocalSession() {
  sessionStorage.removeItem("user-id")
  sessionStorage.removeItem("session-id")
}

export async function logout() {
  removeLocalSession();
  return fetch(`${API}/logout`, {
    method: 'GET',
    credentials: 'include'
  });
}
